import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  useSiteSettings,
  useUpdateSiteSettings,
  SeoSettings,
  OrganizationSettings,
} from '@/hooks/useSiteSettings';
import {
  AdminPageHeader,
  AdminTableSkeleton,
  ImageUploadField,
} from '@/components/admin/shared';
import type { Json } from '@/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface CommunityStatsSettings {
  contributors_count: number | null;
  contributors_label: string;
  schema_reviews_count: number | null;
  schema_reviews_label: string;
  validations_count: number | null;
  validations_label: string;
  use_real_data: boolean;
}

const DEFAULT_COMMUNITY_STATS: CommunityStatsSettings = {
  contributors_count: null,
  contributors_label: 'Contributors',
  schema_reviews_count: null,
  schema_reviews_label: 'Schema Reviews',
  validations_count: null,
  validations_label: 'Validations Run',
  use_real_data: true,
};

export function SiteSettingsManager() {
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useSiteSettings();
  const updateSettings = useUpdateSiteSettings();
  
  const [seoForm, setSeoForm] = useState<SeoSettings | null>(null);
  const [orgForm, setOrgForm] = useState<OrganizationSettings | null>(null);
  const [statsForm, setStatsForm] = useState<CommunityStatsSettings | null>(null);

  // Fetch community stats settings
  const { data: communityStatsData, isLoading: statsLoading } = useQuery({
    queryKey: ['community-stats-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'community_stats')
        .maybeSingle();
      
      if (error) throw error;
      return (data?.value as unknown as CommunityStatsSettings) || DEFAULT_COMMUNITY_STATS;
    },
  });

  // Update community stats mutation
  const updateCommunityStats = useMutation({
    mutationFn: async (value: CommunityStatsSettings) => {
      const { error } = await supabase
        .from('site_settings')
        .update({ value: value as unknown as Json })
        .eq('key', 'community_stats');
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-stats-settings'] });
    },
  });

  // Initialize forms when data loads
  if (settings && !seoForm) {
    setSeoForm(settings.seo);
  }
  if (settings && !orgForm) {
    setOrgForm(settings.organization);
  }
  if (communityStatsData && !statsForm) {
    setStatsForm(communityStatsData);
  }

  const handleSaveSeo = async () => {
    if (!seoForm) return;
    try {
      await updateSettings.mutateAsync({ key: 'seo', value: seoForm as unknown as Json });
      toast.success('SEO settings saved');
    } catch (error) {
      toast.error('Failed to save SEO settings');
    }
  };

  const handleSaveOrg = async () => {
    if (!orgForm) return;
    try {
      await updateSettings.mutateAsync({ key: 'organization', value: orgForm as unknown as Json });
      toast.success('Organization settings saved');
    } catch (error) {
      toast.error('Failed to save organization settings');
    }
  };

  const handleSaveStats = async () => {
    if (!statsForm) return;
    try {
      await updateCommunityStats.mutateAsync(statsForm);
      toast.success('Community stats settings saved');
    } catch (error) {
      toast.error('Failed to save community stats settings');
    }
  };

  if (isLoading || statsLoading) {
    return (
      <div className="space-y-6">
        <AdminPageHeader title="Site Settings" />
        <AdminTableSkeleton columns={2} rows={5} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader title="Site Settings" />

      <Tabs defaultValue="seo" className="space-y-6">
        <TabsList>
          <TabsTrigger value="seo">SEO & Organization</TabsTrigger>
          <TabsTrigger value="community">Community Stats</TabsTrigger>
        </TabsList>

        <TabsContent value="seo">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* SEO Defaults Card */}
            <Card>
              <CardHeader>
                <CardTitle>SEO Defaults</CardTitle>
                <CardDescription>Default meta tags used when page-specific values are not set</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="site_name">Site Name</Label>
                  <Input
                    id="site_name"
                    value={seoForm?.site_name || ''}
                    onChange={(e) => setSeoForm(prev => prev ? { ...prev, site_name: e.target.value } : null)}
                    placeholder="AutomotiveAPI"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="site_tagline">Site Tagline</Label>
                  <Input
                    id="site_tagline"
                    value={seoForm?.site_tagline || ''}
                    onChange={(e) => setSeoForm(prev => prev ? { ...prev, site_tagline: e.target.value } : null)}
                    placeholder="Automotive API Integration Platform"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="default_title">Default Title</Label>
                  <Input
                    id="default_title"
                    value={seoForm?.default_title || ''}
                    onChange={(e) => setSeoForm(prev => prev ? { ...prev, default_title: e.target.value } : null)}
                    placeholder="AutomotiveAPI - Automotive API Integration Platform"
                  />
                  <p className="text-xs text-muted-foreground">Keep under 60 characters</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="default_description">Default Description</Label>
                  <Textarea
                    id="default_description"
                    value={seoForm?.default_description || ''}
                    onChange={(e) => setSeoForm(prev => prev ? { ...prev, default_description: e.target.value } : null)}
                    placeholder="Connect your dealership APIs in minutes..."
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">Keep under 160 characters</p>
                </div>
                
                <ImageUploadField
                  value={seoForm?.default_og_image || ''}
                  onChange={(url) => setSeoForm(prev => prev ? { ...prev, default_og_image: url } : null)}
                  label="Default OG Image"
                  description="Image shown when pages are shared on social media (1200x630px recommended)"
                  storageBucket="seo-images"
                  storageFolder="og"
                />
                
                <div className="space-y-2">
                  <Label htmlFor="twitter_handle">Twitter Handle</Label>
                  <Input
                    id="twitter_handle"
                    value={seoForm?.twitter_handle || ''}
                    onChange={(e) => setSeoForm(prev => prev ? { ...prev, twitter_handle: e.target.value } : null)}
                    placeholder="@AutomotiveAPI"
                  />
                </div>
                
                <Button onClick={handleSaveSeo} disabled={updateSettings.isPending}>
                  Save SEO Settings
                </Button>
              </CardContent>
            </Card>

            {/* Organization Card */}
            <Card>
              <CardHeader>
                <CardTitle>Organization (JSON-LD)</CardTitle>
                <CardDescription>Structured data for search engines</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="org_name">Organization Name</Label>
                  <Input
                    id="org_name"
                    value={orgForm?.name || ''}
                    onChange={(e) => setOrgForm(prev => prev ? { ...prev, name: e.target.value } : null)}
                    placeholder="AutomotiveAPI"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="legal_name">Legal Name</Label>
                  <Input
                    id="legal_name"
                    value={orgForm?.legal_name || ''}
                    onChange={(e) => setOrgForm(prev => prev ? { ...prev, legal_name: e.target.value } : null)}
                    placeholder="AutomotiveAPI Inc."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="org_url">Website URL</Label>
                  <Input
                    id="org_url"
                    value={orgForm?.url || ''}
                    onChange={(e) => setOrgForm(prev => prev ? { ...prev, url: e.target.value } : null)}
                    placeholder="https://automotiveapi.io"
                  />
                </div>
                
                <ImageUploadField
                  value={orgForm?.logo_url || ''}
                  onChange={(url) => setOrgForm(prev => prev ? { ...prev, logo_url: url } : null)}
                  label="Organization Logo"
                  description="Logo for structured data (square, min 112x112px)"
                  storageBucket="seo-images"
                  storageFolder="org"
                />
                
                <div className="space-y-2">
                  <Label htmlFor="founding_date">Founding Date</Label>
                  <Input
                    id="founding_date"
                    type="date"
                    value={orgForm?.founding_date || ''}
                    onChange={(e) => setOrgForm(prev => prev ? { ...prev, founding_date: e.target.value } : null)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="org_description">Description</Label>
                  <Textarea
                    id="org_description"
                    value={orgForm?.description || ''}
                    onChange={(e) => setOrgForm(prev => prev ? { ...prev, description: e.target.value } : null)}
                    placeholder="Open automotive data standards platform"
                    rows={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={orgForm?.contact_email || ''}
                    onChange={(e) => setOrgForm(prev => prev ? { ...prev, contact_email: e.target.value } : null)}
                    placeholder="hello@automotiveapi.io"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Social Links</Label>
                  <div className="space-y-2">
                    <Input
                      value={orgForm?.social_links?.twitter || ''}
                      onChange={(e) => setOrgForm(prev => prev ? { 
                        ...prev, 
                        social_links: { ...prev.social_links, twitter: e.target.value } 
                      } : null)}
                      placeholder="Twitter URL"
                    />
                    <Input
                      value={orgForm?.social_links?.linkedin || ''}
                      onChange={(e) => setOrgForm(prev => prev ? { 
                        ...prev, 
                        social_links: { ...prev.social_links, linkedin: e.target.value } 
                      } : null)}
                      placeholder="LinkedIn URL"
                    />
                    <Input
                      value={orgForm?.social_links?.github || ''}
                      onChange={(e) => setOrgForm(prev => prev ? { 
                        ...prev, 
                        social_links: { ...prev.social_links, github: e.target.value } 
                      } : null)}
                      placeholder="GitHub URL"
                    />
                  </div>
                </div>
                
                <Button onClick={handleSaveOrg} disabled={updateSettings.isPending}>
                  Save Organization Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="community">
          <Card>
            <CardHeader>
              <CardTitle>Community Stats</CardTitle>
              <CardDescription>
                Configure the community statistics shown on the Partners page. 
                Toggle between real-time data or static values.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                <div>
                  <Label className="text-base">Use Real Data</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    When enabled, stats are pulled from the database. Override values below take precedence when set.
                  </p>
                </div>
                <Switch
                  checked={statsForm?.use_real_data ?? true}
                  onCheckedChange={(checked) => setStatsForm(prev => prev ? { ...prev, use_real_data: checked } : null)}
                />
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Contributors */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <h4 className="font-medium">Contributors</h4>
                  <div className="space-y-2">
                    <Label htmlFor="contributors_count">Count Override</Label>
                    <Input
                      id="contributors_count"
                      type="number"
                      value={statsForm?.contributors_count ?? ''}
                      onChange={(e) => setStatsForm(prev => prev ? { 
                        ...prev, 
                        contributors_count: e.target.value ? parseInt(e.target.value) : null 
                      } : null)}
                      placeholder="Leave empty for real data"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contributors_label">Label</Label>
                    <Input
                      id="contributors_label"
                      value={statsForm?.contributors_label ?? 'Contributors'}
                      onChange={(e) => setStatsForm(prev => prev ? { 
                        ...prev, 
                        contributors_label: e.target.value 
                      } : null)}
                    />
                  </div>
                </div>

                {/* Schema Reviews */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <h4 className="font-medium">Schema Reviews</h4>
                  <div className="space-y-2">
                    <Label htmlFor="schema_reviews_count">Count Override</Label>
                    <Input
                      id="schema_reviews_count"
                      type="number"
                      value={statsForm?.schema_reviews_count ?? ''}
                      onChange={(e) => setStatsForm(prev => prev ? { 
                        ...prev, 
                        schema_reviews_count: e.target.value ? parseInt(e.target.value) : null 
                      } : null)}
                      placeholder="Leave empty for real data"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="schema_reviews_label">Label</Label>
                    <Input
                      id="schema_reviews_label"
                      value={statsForm?.schema_reviews_label ?? 'Schema Reviews'}
                      onChange={(e) => setStatsForm(prev => prev ? { 
                        ...prev, 
                        schema_reviews_label: e.target.value 
                      } : null)}
                    />
                  </div>
                </div>

                {/* Validations */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <h4 className="font-medium">Validations</h4>
                  <div className="space-y-2">
                    <Label htmlFor="validations_count">Count Override</Label>
                    <Input
                      id="validations_count"
                      type="number"
                      value={statsForm?.validations_count ?? ''}
                      onChange={(e) => setStatsForm(prev => prev ? { 
                        ...prev, 
                        validations_count: e.target.value ? parseInt(e.target.value) : null 
                      } : null)}
                      placeholder="Leave empty for real data"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="validations_label">Label</Label>
                    <Input
                      id="validations_label"
                      value={statsForm?.validations_label ?? 'Validations Run'}
                      onChange={(e) => setStatsForm(prev => prev ? { 
                        ...prev, 
                        validations_label: e.target.value 
                      } : null)}
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveStats} disabled={updateCommunityStats.isPending}>
                Save Community Stats Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
